/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { StudySite, ClinicalApp } from './types'
import { getEtmfFeature, getRcrFeature } from '../../../services/api/api.settings'
import { useServiceContext } from '../../../root'
import { useRcmTableData } from '../../../hooks/useRcmTableData'
import { RcmTable } from '../../../components/rcm-table/RcmTable'
import { TableHeadRow, TableHeadCell, TableRow } from '../../../components/table/TableComponents'
import { UserRole } from '../../../types/access'

interface SitesTableProps {
  userRole: UserRole
}

export const SitesTable: React.FC<SitesTableProps> = ({ userRole }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { service } = useServiceContext()
  const tableData = useRcmTableData()
  const [isEtmfEnabled, setIsEtmfEnabled] = useState(false)
  const [isRcrEnabled, setIsRcrEnabled] = useState(false)

  // Check feature flags on mount
  useEffect(() => {
    Promise.all([getEtmfFeature(), getRcrFeature()]).then(([etmf, rcr]) => {
      setIsEtmfEnabled(!!etmf)
      setIsRcrEnabled(!!rcr)
    })
  }, [])

  const translateClinicalApp = (app: string): string => {
    if ([ClinicalApp.ETMF, ClinicalApp.RACT, ClinicalApp.SIM].includes(app as ClinicalApp)) {
      return t(`CLINICAL_APPLICATION_${app}`)
    }
    return '—'
  }

  const handleSiteClick = (row: StudySite) => {
    // ETMF_MANAGER, ETMF_SPECIALIST, RCR_USER, ETMF_SPONSOR: Always go to browser
    if (
      [UserRole.ETMF_MANAGER, UserRole.ETMF_SPECIALIST, UserRole.RCR_USER, UserRole.ETMF_SPONSOR].includes(userRole)
    ) {
      navigate(`/browser/${row.fsiId}`)
    }
    // ETMF_SITE_USER: Conditional based on app type
    else if (userRole === UserRole.ETMF_SITE_USER) {
      if (row.app === ClinicalApp.ETMF) {
        service.staging.setSiteId(row.fsiId.toString())
        navigate(`/staging/stagingarea?siteId=${row.fsiId}`)
      } else {
        navigate(`/browser/${row.fsiId}`)
      }
    }
    // Default fallback
    else {
      navigate(`/browser/${row.fsiId}`)
    }
  }

  if (!isEtmfEnabled && !isRcrEnabled) {
    return null
  }

  return (
    <div className="etmf-overview sites-table">
      <RcmTable
        HeadRow={
          <TableHeadRow>
            <TableHeadCell sort="siteName">{t('TABLE_TH_SITE_NAME')}</TableHeadCell>
            <TableHeadCell sort="siteNum">{t('TABLE_TH_STUDY_ENV_SITE_NO')}</TableHeadCell>
            <TableHeadCell sort="studyName">
              {t('TABLE_TH_STUDY_NAME')} ({t('TABLE_TH_ENVIRONMENT')})
            </TableHeadCell>
            <TableHeadCell sort="app">{t('TABLE_TH_APPLICATION')}</TableHeadCell>
          </TableHeadRow>
        }
        Row={(props) => {
          const row = props.row as StudySite
          return (
            <TableRow {...props}>
              <td>
                <a className="cursor-pointer word-break" onClick={() => handleSiteClick(row)}>
                  {row.siteName}
                </a>
              </td>
              <td>{row.siteNum || '—'}</td>
              <td className="name clipped-cell">
                <div className="d-flex align-left">
                  <div className="ellipsis" style={{ minWidth: '100px' }} title={row.studyName}>
                    {row.studyName}
                  </div>
                  {row.envName && (
                    <div className="ellipsis" style={{ minWidth: '50px' }} title={row.envName}>
                      &nbsp;({row.envName})
                    </div>
                  )}
                </div>
              </td>
              <td>{translateClinicalApp(row.app)}</td>
            </TableRow>
          )
        }}
        className="chita-table"
        noResultsText="NO_RESULTS"
      />
    </div>
  )
}
